import { motion } from 'framer-motion'
import { CheckCircle, Users, Target, Zap, Settings, TrendingUp } from 'lucide-react'
import { fadeIn, staggerContainer } from '../utils/motion'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'

const Accompagnement = () => {
  const { language } = useLanguage()
  const t = useTranslation(language)

  const missions = [
    'accompagnement.mission1',
    'accompagnement.mission2',
    'accompagnement.mission3',
    'accompagnement.mission4',
    'accompagnement.mission5',
    'accompagnement.mission6',
    'accompagnement.mission7'
  ]

  const specialties = [
    {
      icon: Target,
      titleKey: 'accompagnement.specialty1Title',
      descriptionKey: 'accompagnement.specialty1Desc'
    },
    {
      icon: Users,
      titleKey: 'accompagnement.specialty2Title',
      descriptionKey: 'accompagnement.specialty2Desc'
    },
    {
      icon: Zap,
      titleKey: 'accompagnement.specialty3Title',
      descriptionKey: 'accompagnement.specialty3Desc'
    }
  ]

  const phases = [
    {
      titleKey: 'accompagnement.phase1Title',
      descriptionKey: 'accompagnement.phase1Desc',
      icon: Settings
    },
    {
      titleKey: 'accompagnement.phase2Title',
      descriptionKey: 'accompagnement.phase2Desc',
      icon: TrendingUp
    }
  ]

  return (
    <div className="pt-26">
      {/* Hero Section avec image */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 to-red-800 text-white overflow-hidden">
        {/* Image de fond */}
        <div className="absolute inset-0">
          <img
            src="/images/accompagnement-hero.webp"
            alt="Accompagnement Opérationnel"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-blue-900/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              {t('accompagnement.pageTitle')}
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              {t('accompagnement.pageSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <motion.div variants={fadeIn('up', 0.2)} className="text-center mb-16">
              <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">
                {t('accompagnement.specialtiesTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('accompagnement.specialtiesSubtitle')}
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
            >
              {specialties.map((specialty, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn('up', index * 0.1)}
                  className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500 to-orange-700 mb-6">
                    <specialty.icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-gray-900 mb-3">
                    {t(specialty.titleKey)}
                  </h3>
                  <p className="text-gray-600">{t(specialty.descriptionKey)}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section avec image au milieu */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contenu Missions */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
            >
              <motion.div variants={fadeIn('up', 0.2)} className="mb-8">
                <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">
                  {t('accompagnement.missionsTitle')}
                </h2>
                <p className="text-xl text-gray-600">
                  {t('accompagnement.missionsSubtitle')}
                </p>
              </motion.div>

              <motion.div
                variants={staggerContainer}
                className="space-y-4"
              >
                {missions.map((missionKey, index) => (
                  <motion.div
                    key={index}
                    variants={fadeIn('up', index * 0.1)}
                    className="flex items-start space-x-4 p-4 bg-white rounded-xl hover:shadow-md transition-shadow duration-300"
                  >
                    <CheckCircle className="text-orange-600 flex-shrink-0 mt-1" size={24} />
                    <p className="text-gray-700 text-lg">{t(missionKey)}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Image au milieu */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/acompeniement1.webp"
                  alt="Accompagnement sur le terrain"
                  className="w-full h-[500px] object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                
                {/* Badge informatif */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                  className="absolute bottom-6 left-6 bg-white rounded-xl p-4 shadow-lg"
                >
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-orange-600" />
                    <span className="font-bold text-gray-900 text-sm">{t('accompagnement.teamIntegration')}</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <motion.div variants={fadeIn('up', 0.2)} className="text-center mb-16">
              <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">
                {t('accompagnement.phasesTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('accompagnement.phasesSubtitle')}
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
            >
              {phases.map((phase, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn('up', index * 0.1)}
                  className="bg-gradient-to-br from-orange-50 to-white p-10 rounded-2xl shadow-lg"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500 to-orange-700 mb-6">
                    <phase.icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
                    {t(phase.titleKey)}
                  </h3>
                  <p className="text-gray-600 text-lg">{t(phase.descriptionKey)}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
<section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/acompeniement2.webp"
                alt="Notre expertise en action"
                className="w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
            
            {/* Élément décoratif */}
            <div className="absolute -z-10 -bottom-8 -right-8 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-20"></div>
            <div className="absolute -z-10 -top-8 -left-8 w-64 h-64 bg-purple-100 rounded-full blur-3xl opacity-20"></div>
          </motion.div>
        </div>
      </section>
      <section className="py-20 bg-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              {t('accompagnement.closingTitle')}
            </h2>
            <p className="text-xl">
              {t('accompagnement.closingText')}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Accompagnement